/**
 * useLandingENKflowLandingGaltonLogic
 *
 * [shared]-entity logic hook.
 * Reads/writes the shared-entity store instead of a per-trait
 * reducer — see @almadar/ui/hooks/useSharedEntityStore.
 * Dispatch resolves transitions + effects fully client-side (no
 * server round trip).
 *
 * @generated from schema
 * @trait LandingENKflowLandingGalton
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useSharedEntityStoreContext, useSharedEntitySnapshot, useEventBus, useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { emitCascadeEvent } from '@/hooks/useOrbitalBridge';
import type { EntityFieldWrite, EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import { mergeEntityFrame } from '@almadar/core';
import type { BusEvent } from '@almadar/core';
import type { EventResponse, LandingENGaltonState, LandingENKflowLandingGaltonEventPayloadMap } from '@app/shared';

export interface LandingENKflowLandingGaltonConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  backgroundColor: string;
  ballCount: number;
  binCount: number;
  floorFrac: number;
  gravity: number;
  height: number;
  histHeight: number;
  jitter: number;
  matchMode: string;
  pegRows: number;
  substeps: number;
  width: number;
}

const DEFAULT_LANDING_EN_KFLOW_LANDING_GALTON_CONFIG = {
  backgroundColor: "",
  ballCount: 16,
  binCount: 9,
  floorFrac: 0.95,
  gravity: 0.5,
  height: 400,
  histHeight: 160,
  jitter: 5,
  matchMode: "",
  pegRows: 5,
  substeps: 6,
  width: 600,
} as LandingENKflowLandingGaltonConfig;

type LandingENKflowLandingGaltonSlot = 'main';
export interface LandingENKflowLandingGaltonLogicReturn {
  state: string;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: string | null;
  slotWriter: Partial<Record<LandingENKflowLandingGaltonSlot, string | null>>;
  loading: boolean;
  pendingEvent: string | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<LandingENGaltonState>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof LandingENKflowLandingGaltonEventPayloadMap]: (payload?: LandingENKflowLandingGaltonEventPayloadMap[K]) => Promise<EventResponse> };
  config: LandingENKflowLandingGaltonConfig;
}

export function useLandingENKflowLandingGaltonLogic(config?: Partial<LandingENKflowLandingGaltonConfig>): LandingENKflowLandingGaltonLogicReturn {
  const mergedConfig: LandingENKflowLandingGaltonConfig = { ...DEFAULT_LANDING_EN_KFLOW_LANDING_GALTON_CONFIG, ...(config ?? {}) };

  // [shared] entity store + coalesced tick driver
  const _sharedStore = useSharedEntityStoreContext();
  const _sharedEntityId = 'LandingENGaltonState';
  _sharedStore.seed(_sharedEntityId, { 'active': false, 'running': false, 'steps': 0, 'elapsedMs': 0, 'done': false, 'histMax': 1.25, 'speed': 1 });
  const _sharedSnapshot = useSharedEntitySnapshot(_sharedStore, _sharedEntityId);

  const fields = _sharedSnapshot as Partial<LandingENGaltonState>;
  const data: Record<string, EntityRow[]> = { 'LandingENGaltonState': [_sharedSnapshot] };

  // Local per-trait machine state — NOT the shared entity's fields,
  // which always live in the shared store (read fresh on every
  // dispatch, never cached here).
  const [machineState, setMachineState] = useState<string>('idle');
  const [lastPayload, setLastPayload] = useState<EventPayload | undefined>(undefined);
  const [lastEvent, setLastEvent] = useState<string | null>(null);
  const [slotWriter, setSlotWriter] = useState<Partial<Record<LandingENKflowLandingGaltonSlot, string | null>>>({});
  const [error, setError] = useState<string | null>(null);
  const machineStateRef = useRef(machineState);
  machineStateRef.current = machineState;

  const eventBus = useEventBus();

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const payloadObj = payload;
    const entity: EntityRow[] = liveData['LandingENGaltonState'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};


    if (fromState === 'idle' && event === 'INIT' && (true)) {
      // idle → idle (on INIT)
        {

        const centerX = (600 / 2);

        const topY = (400 * 0.05);

        const spawnScatter = (600 * 0.01333);

        const floorY = (400 * 0.95);

        const pegTopY = (400 * 0.225);

        const pegBottomY = (400 * 0.775);

        const pegRowGap = ((+(pegBottomY ?? 0) - +(pegTopY ?? 0)) / (5 - 1));

        const pegColGap = (600 * 0.0733);

        const pegs = (Array.from({ length: Math.ceil(5) }, (_, i) => i)).reduce<EventPayloadValue>((acc, r) => [...((acc as readonly EventPayloadValue[] | undefined) ?? []), ...((Array.from({ length: Math.ceil((+(r ?? 0) + 1)) }, (_, i) => i)).map(((c) => ({ type: "circle" as const, x: (+(centerX ?? 0) + ((+(c ?? 0) - (+(r ?? 0) / 2)) * +(pegColGap ?? 0))), y: (+(pegTopY ?? 0) + (+(r ?? 0) * +(pegRowGap ?? 0))), radius: 4, color: "muted" as const, fill: "muted" as const }))))], []);

        const walls = [{ kind: "wall" as const, x: 0, y1: 0, y2: floorY, color: "axis" as const }, { kind: "wall" as const, x: 600, y1: 0, y2: floorY, color: "axis" as const }];

        const bodies = (Array.from({ length: Math.ceil(16) }, (_, i) => i)).map(((i) => ({ id: ["b", ["", i].join('')].join(''), x: (+(centerX ?? 0) + ((Math.random() * (2 * +(spawnScatter ?? 0))) - +(spawnScatter ?? 0))), y: (+(topY ?? 0) + (+(i ?? 0) * 2)), radius: 6, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 })));

        const zeroBins = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => 0));

        const histBars = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => ({ x: i, width: 0.8, y1: 0, color: "series-1" as const, opacity: 0.7 })));

        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pegs'] = pegs;
        fields = { ...(fields ?? {}), 'pegs': _fields['pegs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['walls'] = walls;
        fields = { ...(fields ?? {}), 'walls': _fields['walls'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = bodies;
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['binCounts'] = zeroBins;
        fields = { ...(fields ?? {}), 'binCounts': _fields['binCounts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histBars'] = histBars;
        fields = { ...(fields ?? {}), 'histBars': _fields['histBars'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histMax'] = 1.25;
        fields = { ...(fields ?? {}), 'histMax': _fields['histMax'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "landed" as const, value: "0" as const, color: "ink" as const }, { label: "tallest bin" as const, value: "-" as const, color: "highlight" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = "Press Step to drop a batch of balls, or Run to auto-drop.";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('GALTON_UPDATED', _p, { trait: 'LandingENKflowLandingGalton' }); emitCascadeEvent(eventBus, 'LandingEN', 'LandingENKflowLandingGalton', { event: 'GALTON_UPDATED', payload: _p, source: { orbital: 'LandingEN', trait: 'LandingENKflowLandingGalton' } }); })({ bodies: fields?.bodies, binCounts: fields?.binCounts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((!true && !(payloadObj?.mode === "")))) {
      // idle → idle (on MODE_SELECTED)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'MODE_SELECTED' && ((payloadObj?.mode === ""))) {
      // idle → idle (on MODE_SELECTED)
        {

        const centerX = (600 / 2);

        const topY = (400 * 0.05);

        const spawnScatter = (600 * 0.01333);

        const floorY = (400 * 0.95);

        const pegTopY = (400 * 0.225);

        const pegBottomY = (400 * 0.775);

        const pegRowGap = ((+(pegBottomY ?? 0) - +(pegTopY ?? 0)) / (5 - 1));

        const pegColGap = (600 * 0.0733);

        const pegs = (Array.from({ length: Math.ceil(5) }, (_, i) => i)).reduce<EventPayloadValue>((acc, r) => [...((acc as readonly EventPayloadValue[] | undefined) ?? []), ...((Array.from({ length: Math.ceil((+(r ?? 0) + 1)) }, (_, i) => i)).map(((c) => ({ type: "circle" as const, x: (+(centerX ?? 0) + ((+(c ?? 0) - (+(r ?? 0) / 2)) * +(pegColGap ?? 0))), y: (+(pegTopY ?? 0) + (+(r ?? 0) * +(pegRowGap ?? 0))), radius: 4, color: "muted" as const, fill: "muted" as const }))))], []);

        const walls = [{ kind: "wall" as const, x: 0, y1: 0, y2: floorY, color: "axis" as const }, { kind: "wall" as const, x: 600, y1: 0, y2: floorY, color: "axis" as const }];

        const bodies = (Array.from({ length: Math.ceil(16) }, (_, i) => i)).map(((i) => ({ id: ["b", ["", i].join('')].join(''), x: (+(centerX ?? 0) + ((Math.random() * (2 * +(spawnScatter ?? 0))) - +(spawnScatter ?? 0))), y: (+(topY ?? 0) + (+(i ?? 0) * 2)), radius: 6, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 })));

        const zeroBins = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => 0));

        const histBars = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => ({ x: i, width: 0.8, y1: 0, color: "series-1" as const, opacity: 0.7 })));

        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pegs'] = pegs;
        fields = { ...(fields ?? {}), 'pegs': _fields['pegs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['walls'] = walls;
        fields = { ...(fields ?? {}), 'walls': _fields['walls'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = bodies;
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['binCounts'] = zeroBins;
        fields = { ...(fields ?? {}), 'binCounts': _fields['binCounts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histBars'] = histBars;
        fields = { ...(fields ?? {}), 'histBars': _fields['histBars'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histMax'] = 1.25;
        fields = { ...(fields ?? {}), 'histMax': _fields['histMax'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "landed" as const, value: "0" as const, color: "ink" as const }, { label: "tallest bin" as const, value: "-" as const, color: "highlight" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = "Press Step to drop a batch of balls, or Run to auto-drop.";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('GALTON_UPDATED', _p, { trait: 'LandingENKflowLandingGalton' }); emitCascadeEvent(eventBus, 'LandingEN', 'LandingENKflowLandingGalton', { event: 'GALTON_UPDATED', payload: _p, source: { orbital: 'LandingEN', trait: 'LandingENKflowLandingGalton' } }); })({ bodies: fields?.bodies, binCounts: fields?.binCounts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_STEP' && (((fields?.active === true) && !(fields?.done === true)))) {
      // idle → idle (on LEARN_STEP)
        {

        const floorY = (400 * 0.95);

        const centerX = (600 / 2);

        const topY = (400 * 0.05);

        const spawnScatter = (600 * 0.01333);

        const result = (Array.from({ length: Math.ceil(6) }, (_, i) => i)).reduce<EventPayloadValue>((acc, s) => (() => { const stepResult = ((getNestedValue(acc, "bodies") as readonly EventPayloadValue[] | undefined) ?? []).reduce<EventPayloadValue>((acc2, b) => (() => { const y = getNestedValue(b, "y"); const x = getNestedValue(b, "x"); const vy = getNestedValue(b, "vy"); const radius = getNestedValue(b, "radius"); const nvy = (+(vy ?? 0) + 0.5); const ny = (+(y ?? 0) + +(nvy ?? 0)); const nx = Math.min(Math.max(((+(x ?? 0) + ((Math.random() * (2 * 5)) - 5))), Number(radius ?? 0)), ((600 - +(radius ?? 0)))); return ((+(ny ?? 0) >= +(floorY ?? 0)) ? (() => { const binIdx = Math.min(Math.max((Math.floor((((+(nx ?? 0) / 600) * 9)))), (0)), ((9 - 1))); const curBinCounts = getNestedValue(acc2, "binCounts"); const newBinCounts = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => ((i === binIdx) ? (((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(curBinCounts, Number(i ?? 0)) + 1) : ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(curBinCounts, Number(i ?? 0))))); const respawned = Object.assign({}, b, { x: (+(centerX ?? 0) + ((Math.random() * (2 * +(spawnScatter ?? 0))) - +(spawnScatter ?? 0))), y: topY, vy: 0 }); return { bodies: [...((getNestedValue(acc2, "bodies") as readonly EventPayloadValue[] | undefined) ?? []), respawned], binCounts: newBinCounts }; })() : { bodies: [...((getNestedValue(acc2, "bodies") as readonly EventPayloadValue[] | undefined) ?? []), Object.assign({}, b, { x: nx, y: ny, vy: nvy })], binCounts: getNestedValue(acc2, "binCounts") }); })(), { bodies: [], binCounts: getNestedValue(acc, "binCounts") }); return stepResult; })(), { bodies: fields?.bodies, binCounts: fields?.binCounts });

        const newBodies = getNestedValue(result, "bodies");

        const newBinCounts = getNestedValue(result, "binCounts");

        const histBars = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => ({ x: i, width: 0.8, y1: ((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(newBinCounts, Number(i ?? 0)), color: "series-1" as const, opacity: 0.7 })));

        const histMax = (Math.max((1), Number(((ns: readonly number[]) => (ns.length ? Math.max(...ns) : null))(((vs: readonly EventPayloadValue[], key: string | undefined): number[] => { const isRecord = (x: EventPayloadValue): x is Readonly<Record<string, EventPayloadValue>> => x !== null && typeof x === 'object' && !Array.isArray(x) && !(x instanceof Date); return vs.flatMap((e) => { const v = key === undefined ? e : isRecord(e) && key in e ? e[key] : undefined; return typeof v === 'number' ? [v] : typeof v === 'boolean' ? [v ? 1 : 0] : v === null ? [0] : typeof v === 'string' && v !== '' && !Number.isNaN(Number(v)) ? [Number(v)] : []; }); })((((histBars ?? []) as readonly EntityRow[]).map((b) => getNestedValue(b, "y1"))), undefined)) ?? 0)) * 1.25);

        const totalBefore = ((fields?.binCounts as readonly EventPayloadValue[] | undefined) ?? []).reduce<EventPayloadValue>((acc, c) => (+(acc ?? 0) + +(c ?? 0)), 0);

        const totalAfter = ((newBinCounts as readonly EventPayloadValue[] | undefined) ?? []).reduce<EventPayloadValue>((acc, c) => (+(acc ?? 0) + +(c ?? 0)), 0);

        const landedThisStep = (+(totalAfter ?? 0) - +(totalBefore ?? 0));

        const maxCount = ((ns: readonly number[]) => (ns.length ? Math.max(...ns) : null))(((vs: readonly EventPayloadValue[], key: string | undefined): number[] => { const isRecord = (x: EventPayloadValue): x is Readonly<Record<string, EventPayloadValue>> => x !== null && typeof x === 'object' && !Array.isArray(x) && !(x instanceof Date); return vs.flatMap((e) => { const v = key === undefined ? e : isRecord(e) && key in e ? e[key] : undefined; return typeof v === 'number' ? [v] : typeof v === 'boolean' ? [v ? 1 : 0] : v === null ? [0] : typeof v === 'string' && v !== '' && !Number.isNaN(Number(v)) ? [Number(v)] : []; }); })(((newBinCounts as readonly EventPayloadValue[] | undefined) ?? []), undefined));

        const tallestIdx = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).reduce<EventPayloadValue>((acc, i) => ((((__a: EventPayloadValue | undefined, __i: number | undefined) => Array.isArray(__a) ? __a[__i ?? 0] : undefined)(newBinCounts, Number(i ?? 0)) === maxCount) ? i : acc), 0);

        const narration = [["", landedThisStep].join(''), " ball(s) landed this step — bin ", ["", tallestIdx].join(''), " leads with ", ["", maxCount].join(''), " total"].join('');

        _fields['bodies'] = newBodies;
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['binCounts'] = newBinCounts;
        fields = { ...(fields ?? {}), 'binCounts': _fields['binCounts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histBars'] = histBars;
        fields = { ...(fields ?? {}), 'histBars': _fields['histBars'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histMax'] = histMax;
        fields = { ...(fields ?? {}), 'histMax': _fields['histMax'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "landed" as const, value: ["", totalAfter].join(''), color: "ink" as const }, { label: "tallest bin" as const, value: ["", tallestIdx].join(''), color: "highlight" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = narration;
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('GALTON_UPDATED', _p, { trait: 'LandingENKflowLandingGalton' }); emitCascadeEvent(eventBus, 'LandingEN', 'LandingENKflowLandingGalton', { event: 'GALTON_UPDATED', payload: _p, source: { orbital: 'LandingEN', trait: 'LandingENKflowLandingGalton' } }); })({ bodies: fields?.bodies, binCounts: fields?.binCounts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_RESET' && ((fields?.active === true))) {
      // idle → idle (on LEARN_RESET)
        {

        const centerX = (600 / 2);

        const topY = (400 * 0.05);

        const spawnScatter = (600 * 0.01333);

        const floorY = (400 * 0.95);

        const pegTopY = (400 * 0.225);

        const pegBottomY = (400 * 0.775);

        const pegRowGap = ((+(pegBottomY ?? 0) - +(pegTopY ?? 0)) / (5 - 1));

        const pegColGap = (600 * 0.0733);

        const pegs = (Array.from({ length: Math.ceil(5) }, (_, i) => i)).reduce<EventPayloadValue>((acc, r) => [...((acc as readonly EventPayloadValue[] | undefined) ?? []), ...((Array.from({ length: Math.ceil((+(r ?? 0) + 1)) }, (_, i) => i)).map(((c) => ({ type: "circle" as const, x: (+(centerX ?? 0) + ((+(c ?? 0) - (+(r ?? 0) / 2)) * +(pegColGap ?? 0))), y: (+(pegTopY ?? 0) + (+(r ?? 0) * +(pegRowGap ?? 0))), radius: 4, color: "muted" as const, fill: "muted" as const }))))], []);

        const walls = [{ kind: "wall" as const, x: 0, y1: 0, y2: floorY, color: "axis" as const }, { kind: "wall" as const, x: 600, y1: 0, y2: floorY, color: "axis" as const }];

        const bodies = (Array.from({ length: Math.ceil(16) }, (_, i) => i)).map(((i) => ({ id: ["b", ["", i].join('')].join(''), x: (+(centerX ?? 0) + ((Math.random() * (2 * +(spawnScatter ?? 0))) - +(spawnScatter ?? 0))), y: (+(topY ?? 0) + (+(i ?? 0) * 2)), radius: 6, color: "series-1" as const, label: "" as const, vx: 0, vy: 0, fx: 0, fy: 0 })));

        const zeroBins = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => 0));

        const histBars = (Array.from({ length: Math.ceil(9) }, (_, i) => i)).map(((i) => ({ x: i, width: 0.8, y1: 0, color: "series-1" as const, opacity: 0.7 })));

        _fields['done'] = false;
        fields = { ...(fields ?? {}), 'done': _fields['done'] } as Record<string, EventPayloadValue | undefined>;
        _fields['pegs'] = pegs;
        fields = { ...(fields ?? {}), 'pegs': _fields['pegs'] } as Record<string, EventPayloadValue | undefined>;
        _fields['walls'] = walls;
        fields = { ...(fields ?? {}), 'walls': _fields['walls'] } as Record<string, EventPayloadValue | undefined>;
        _fields['bodies'] = bodies;
        fields = { ...(fields ?? {}), 'bodies': _fields['bodies'] } as Record<string, EventPayloadValue | undefined>;
        _fields['binCounts'] = zeroBins;
        fields = { ...(fields ?? {}), 'binCounts': _fields['binCounts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histBars'] = histBars;
        fields = { ...(fields ?? {}), 'histBars': _fields['histBars'] } as Record<string, EventPayloadValue | undefined>;
        _fields['histMax'] = 1.25;
        fields = { ...(fields ?? {}), 'histMax': _fields['histMax'] } as Record<string, EventPayloadValue | undefined>;
        _fields['readouts'] = [{ label: "landed" as const, value: "0" as const, color: "ink" as const }, { label: "tallest bin" as const, value: "-" as const, color: "highlight" as const }];
        fields = { ...(fields ?? {}), 'readouts': _fields['readouts'] } as Record<string, EventPayloadValue | undefined>;
        _fields['narration'] = "Press Step to drop a batch of balls, or Run to auto-drop.";
        fields = { ...(fields ?? {}), 'narration': _fields['narration'] } as Record<string, EventPayloadValue | undefined>;
        }

        _emits.push(((_p) => () => { eventBus.emit('GALTON_UPDATED', _p, { trait: 'LandingENKflowLandingGalton' }); emitCascadeEvent(eventBus, 'LandingEN', 'LandingENKflowLandingGalton', { event: 'GALTON_UPDATED', payload: _p, source: { orbital: 'LandingEN', trait: 'LandingENKflowLandingGalton' } }); })({ bodies: fields?.bodies, binCounts: fields?.binCounts, narration: fields?.narration }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'LEARN_UPDATED' && ((fields?.active === true))) {
      // idle → idle (on LEARN_UPDATED)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: LandingENKflowLandingGaltonSlot[]; clear: LandingENKflowLandingGaltonSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as LandingENKflowLandingGaltonSlot[], clear: [] as LandingENKflowLandingGaltonSlot[] };
          case 'LEARN_RESET': return { write: ['main'] as LandingENKflowLandingGaltonSlot[], clear: [] as LandingENKflowLandingGaltonSlot[] };
          case 'LEARN_STEP': return { write: ['main'] as LandingENKflowLandingGaltonSlot[], clear: [] as LandingENKflowLandingGaltonSlot[] };
          case 'LEARN_UPDATED': return { write: ['main'] as LandingENKflowLandingGaltonSlot[], clear: [] as LandingENKflowLandingGaltonSlot[] };
          case 'MODE_SELECTED': return { write: ['main'] as LandingENKflowLandingGaltonSlot[], clear: ['main'] as LandingENKflowLandingGaltonSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event against the shared entity, committing locally
   * first (the fields mirror IS the `[runtime]` row).
   * No server round trip — this trait declares `local`.
   */
  const dispatch = useCallback(async (event: string, payload?: EventPayload): Promise<EventResponse> => {
    const prevState = machineStateRef.current;
    const liveData: Record<string, EntityRow[]> = { 'LandingENGaltonState': [_sharedStore.getSnapshot(_sharedEntityId)] };
    const result = executeTransitionEffects(prevState, event, liveData, payload);
    if (result.newState === undefined) {
      const errorMsg = `No transition defined for ${event} from ${prevState}`;
      setError(errorMsg);
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const writes: EntityFieldWrite[] = Object.entries(result.fields)
      .filter(([, value]) => value !== undefined)
      .map(([field, value]) => ({ field, value: value as FieldValue }));
    if (writes.length > 0) {
      _sharedStore.commit(_sharedEntityId, mergeEntityFrame(_sharedStore.getSnapshot(_sharedEntityId), writes));
    }
    for (const _emit of result.emits) _emit();
    machineStateRef.current = result.newState;
    setMachineState(result.newState);
    const _renderSlots = getRenderUiSlots(prevState, event);
    if (_renderSlots.write.length > 0 || _renderSlots.clear.length > 0) {
      setLastPayload(payload);
      setLastEvent(event);
      setSlotWriter((prev) => ({
        ...prev,
        ...Object.fromEntries(_renderSlots.write.map((s) => [s, event] as const)),
        ...Object.fromEntries(_renderSlots.clear.map((s) => [s, null] as const)),
      }));
    }
    setError(null);
    return { success: true, transitioned: true, states: { 'LandingENKflowLandingGalton': result.newState }, emittedEvents: [] } as EventResponse;
  }, [executeTransitionEffects, getRenderUiSlots, _sharedStore, _sharedEntityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof LandingENKflowLandingGaltonEventPayloadMap]: (payload?: LandingENKflowLandingGaltonEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'MODE_SELECTED': (payload?: LandingENKflowLandingGaltonEventPayloadMap['MODE_SELECTED']) => dispatch('MODE_SELECTED', payload),
    'LEARN_STEP': (payload?: LandingENKflowLandingGaltonEventPayloadMap['LEARN_STEP']) => dispatch('LEARN_STEP', payload),
    'LEARN_RESET': () => dispatch('LEARN_RESET'),
    'LEARN_UPDATED': (payload?: LandingENKflowLandingGaltonEventPayloadMap['LEARN_UPDATED']) => dispatch('LEARN_UPDATED', payload),
    'GALTON_UPDATED': (payload?: LandingENKflowLandingGaltonEventPayloadMap['GALTON_UPDATED']) => dispatch('GALTON_UPDATED', payload),
    'RESET': () => dispatch('RESET'),
    'STEP': () => dispatch('STEP'),
    'TOGGLE_RUN': () => dispatch('TOGGLE_RUN'),
  };

  useUIEvents(enqueueEvent, 'LandingEN.LandingENKflowLandingGalton', ['INIT', 'MODE_SELECTED', 'LEARN_STEP', 'LEARN_RESET', 'LEARN_UPDATED'] as const, eventBus);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'LEARN_RESET' (from LandingENKflowLandingGaltonClock) → trigger 'LEARN_RESET'
    const unsub_learn_reset_from_landingenkflowlandinggaltonclock_to_learn_reset = eventBus.on('UI:LandingEN.LandingENKflowLandingGaltonClock.LEARN_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingEN.LandingENKflowLandingGalton', 'LEARN_RESET')) return;
      enqueueEvent('LEARN_RESET', event.payload);
    });
    // Listen for 'LEARN_STEP' (from LandingENKflowLandingGaltonClock) → trigger 'LEARN_STEP'
    const unsub_learn_step_from_landingenkflowlandinggaltonclock_to_learn_step = eventBus.on('UI:LandingEN.LandingENKflowLandingGaltonClock.LEARN_STEP', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingEN.LandingENKflowLandingGalton', 'LEARN_STEP')) return;
      enqueueEvent('LEARN_STEP', event.payload);
    });
    // Listen for 'LEARN_UPDATED' (from LandingENKflowLandingGaltonClock) → trigger 'LEARN_UPDATED'
    const unsub_learn_updated_from_landingenkflowlandinggaltonclock_to_learn_updated = eventBus.on('UI:LandingEN.LandingENKflowLandingGaltonClock.LEARN_UPDATED', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'LandingEN.LandingENKflowLandingGalton', 'LEARN_UPDATED')) return;
      enqueueEvent('LEARN_UPDATED', event.payload);
    });
    return () => {
      if (typeof unsub_learn_reset_from_landingenkflowlandinggaltonclock_to_learn_reset === 'function') unsub_learn_reset_from_landingenkflowlandinggaltonclock_to_learn_reset();
      if (typeof unsub_learn_step_from_landingenkflowlandinggaltonclock_to_learn_step === 'function') unsub_learn_step_from_landingenkflowlandinggaltonclock_to_learn_step();
      if (typeof unsub_learn_updated_from_landingenkflowlandinggaltonclock_to_learn_updated === 'function') unsub_learn_updated_from_landingenkflowlandinggaltonclock_to_learn_updated();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state: machineState,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading: false,
    pendingEvent: null,
    pendingFrom: null,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
